import { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { NavLink, useLocation } from 'react-router-dom';
import { useClickOutside } from '@mantine/hooks';
import { AccountCenterThemeEvent, SgwtAccountCenter } from '@sgwt/sgwt-widgets-react';
import { clsx } from 'clsx';
import { hasPermission, UserPermission } from '../../services/rights';
import { GlobalContext } from '../../context/GlobalContext';

export const Header = () => {
  const { isInIframe } = useContext(GlobalContext);
  const location = useLocation();
  const [admin, setAdmin] = useState(false);
  const [show, setShow] = useState(false);
  const [isDeclarePath, setIsDeclarePath] = useState(false);

  useEffect(() => {
    hasPermission(UserPermission.ADMINISTRATION_PERMISSION).then((isAdmin: boolean) => setAdmin(isAdmin));
  }, []);

  useEffect(() => {
    setIsDeclarePath(location.pathname.startsWith('/add'));
  }, [location]);

  const handleDropdownToggle = useCallback(() => {
    setShow((prevShow) => !prevShow);
  }, []);

  const handleClickOutside = useCallback(() => {
    setShow(false);
  }, []);

  const changeTheme = useCallback((evt: AccountCenterThemeEvent) => {
    window.loadSgBootstrap(window.SG_BOOTSTRAP_VERSION, evt.theme);
  }, []);

  const dropdownRef = useClickOutside(handleClickOutside);

  return (
    <nav className={`navbar${isInIframe ? ' d-none' : ''}`}>
      <div className="navbar-title">
        <NavLink to="/" className="navbar-title-link">
          <div className="navbar-logo">
            <img
              src="https://shared.sgmarkets.com/assets/images/socgen_logo_full.svg"
              height="32"
              alt="SG Markets"
              className="d-none d-md-block"
            />
            <img
              src="https://shared.sgmarkets.com/assets/images/socgen_logo_muted.svg"
              height="32"
              alt="SG Markets"
              className="d-block d-md-none"
            />
          </div>
          <div className="navbar-title-divider" />
          <div className="navbar-service-name">
            EUC
            <br />
            Catalogue
          </div>
        </NavLink>
      </div>

      <div className="navbar-content">
        <ul className="navbar-navigation m-0">
          <li className="navbar-item platforms-link">
            <NavLink to="/explore/platforms " className="navbar-link nav-menu-platforms">
              <FormattedMessage id="menu.platforms" />
            </NavLink>
          </li>
          <li className="navbar-item explore-link">
            <NavLink to="/explore/all" className="navbar-link nav-menu-explore">
              <FormattedMessage id="menu.explore" />
            </NavLink>
          </li>
          <li ref={dropdownRef} className="navbar-item declare-link dropdown">
            <button
              aria-expanded={show}
              className={clsx('navbar-link dropdown-toggle', show && 'show', isDeclarePath && 'active')}
              data-bs-toggle="dropdown"
              onClick={handleDropdownToggle}
              type="button"
            >
              <FormattedMessage id="menu.declare" />
            </button>
            <ul className={clsx('dropdown-menu', show && 'show')} data-bs-popper={show ? 'static' : 'none'}>
              <NavLink className="dropdown-item" to="/add/euc" onClick={handleClickOutside}>
                <FormattedMessage id="menu.declare.euc" />
              </NavLink>
              <NavLink className="dropdown-item" to="/add/project" onClick={handleClickOutside}>
                <FormattedMessage id="menu.declare.project" />
              </NavLink>
            </ul>
          </li>
          <li className="navbar-item certification-link">
            <NavLink to="/certification" className="navbar-link nav-menu-certification">
              <FormattedMessage id="menu.certification" />
            </NavLink>
          </li>
          <li className="navbar-item monitoring-link">
            <NavLink to="/monitoring" className="navbar-link nav-menu-monitoring">
              <FormattedMessage id="menu.monitoring" />
            </NavLink>
          </li>
          <li className="navbar-item documentation-link">
            <NavLink to="/documentation" className="navbar-link nav-menu-documentation">
              <FormattedMessage id="menu.documentation" />
            </NavLink>
          </li>
          <li className="navbar-item download-link">
            <NavLink to="/download" className="navbar-link nav-menu-download">
              <FormattedMessage id="menu.download" />
            </NavLink>
          </li>
          {admin && (
            <li className="navbar-item certification-link">
              <NavLink to="/administration" className="navbar-link nav-menu-administration">
                <FormattedMessage id="menu.administration" />
              </NavLink>
            </li>
          )}
        </ul>
      </div>

      <div className="user-toolbar">
        <SgwtAccountCenter
          availableLanguages={['en', 'fr']}
          authentication="sg-connect"
          mode="sg-markets"
          environment={
            window.globalConfiguration.appEnvironment === 'production' ? '' : window.globalConfiguration.appEnvironment
          }
          themeSwitcher
          onThemeChanged={changeTheme}
        />
      </div>
    </nav>
  );
};
